import clsx from "clsx";
import { FunctionComponent, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { DEV_OVERLAY_MESSAGING_SLOT_ID } from "src/components/dev/DevOverlayStack";
import { DevPanelCloseIcon } from "src/components/dev/DevPanelIcons";
import {
  DEV_MESSAGING_IN_PROGRESS_DISMISS_KEY,
  DEV_MESSAGING_PASSED_DISMISS_KEY,
  DEV_MESSAGING_WARNINGS_DISMISS_KEY,
  readDevPanelDismissed,
  writeDevPanelDismissed,
} from "src/utils/dev-panel-session";

import * as styles from "./DevMessaging.css";

export interface DevMessagingProps {
  /** Warning messages to show when in development. */
  warnings: string[];
  /** Labels/messages for checks that passed; shown in passed panel when all pass. */
  passedCheckLabels?: string[];
  /** Messages to show in a yellow panel while checks are running (e.g. "A11y checking the page..."). Omit or empty to hide. */
  inProgressMessages?: string[] | null;
}

/**
 * Renders dev check panels portaled into DevOverlayStack. Call via DevMessages only.
 */
const DevMessaging: FunctionComponent<DevMessagingProps> = ({
  warnings,
  passedCheckLabels = [],
  inProgressMessages = null,
}) => {
  const [warningsDismissed, setWarningsDismissed] = useState(false);
  const [inProgressDismissed, setInProgressDismissed] = useState(false);
  const [passedDismissed, setPassedDismissed] = useState(false);
  const [messagingSlot, setMessagingSlot] = useState<HTMLElement | null>(null);

  const inProgressList =
    inProgressMessages !== null
      ? inProgressMessages.filter((m) => String(m).trim() !== "")
      : [];
  const showInProgress = inProgressList.length > 0;

  useEffect(() => {
    setMessagingSlot(document.getElementById(DEV_OVERLAY_MESSAGING_SLOT_ID));
  }, []);

  useEffect(() => {
    if (readDevPanelDismissed(DEV_MESSAGING_WARNINGS_DISMISS_KEY)) {
      setWarningsDismissed(true);
    }
    if (readDevPanelDismissed(DEV_MESSAGING_IN_PROGRESS_DISMISS_KEY)) {
      setInProgressDismissed(true);
    }
    if (readDevPanelDismissed(DEV_MESSAGING_PASSED_DISMISS_KEY)) {
      setPassedDismissed(true);
    }
  }, []);

  if (!messagingSlot) {
    return null;
  }

  const hasWarnings = warnings.length > 0 && !warningsDismissed;
  const showPassed = !passedDismissed && passedCheckLabels.length > 0;
  const hasPanels =
    hasWarnings || (showInProgress && !inProgressDismissed) || showPassed;

  if (!hasPanels) {
    return null;
  }

  return createPortal(
    <>
      {hasWarnings && (
        <aside
          className={clsx(styles.panel, styles.warningPanel)}
          role="status"
          aria-live="polite"
          aria-label="Development warnings"
        >
          <div className={styles.header}>
            <span className={styles.label}>
              {warnings.length > 1 ? "warnings" : "warning"}
            </span>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                writeDevPanelDismissed(DEV_MESSAGING_WARNINGS_DISMISS_KEY);
                setWarningsDismissed(true);
              }}
              aria-label="Close dev warnings"
            >
              <DevPanelCloseIcon />
            </button>
          </div>
          <div className={styles.messages}>
            {warnings.map((msg, i) => (
              <p key={i} className={styles.message}>
                {msg}
              </p>
            ))}
          </div>
        </aside>
      )}
      {showInProgress && !inProgressDismissed && (
        <aside
          className={clsx(styles.panel, styles.inProgressPanel)}
          role="status"
          aria-live="polite"
          aria-label="Checks in progress"
        >
          <div className={styles.header}>
            <span className={styles.label}>Ongoing checks</span>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                writeDevPanelDismissed(DEV_MESSAGING_IN_PROGRESS_DISMISS_KEY);
                setInProgressDismissed(true);
              }}
              aria-label="Close checks in progress"
            >
              <DevPanelCloseIcon />
            </button>
          </div>
          <div className={styles.messages}>
            {inProgressList.map((msg, i) => (
              <p key={i} className={styles.message}>
                {msg}
              </p>
            ))}
          </div>
        </aside>
      )}
      {!passedDismissed && passedCheckLabels.length > 0 && (
        <aside
          className={clsx(styles.panel, styles.passedPanel)}
          role="status"
          aria-label="Development checks passed"
        >
          <div className={styles.header}>
            <span className={styles.label}>Checks passed</span>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => {
                writeDevPanelDismissed(DEV_MESSAGING_PASSED_DISMISS_KEY);
                setPassedDismissed(true);
              }}
              aria-label="Close checks passed"
            >
              <DevPanelCloseIcon />
            </button>
          </div>
          {passedCheckLabels.length > 0 && (
            <ul className={styles.passedChecks} aria-label="Passed checks">
              {passedCheckLabels.map((label) => (
                <li key={label} className={styles.passedCheckItem}>
                  {label}
                </li>
              ))}
            </ul>
          )}
        </aside>
      )}
    </>,
    messagingSlot,
  );
};

export default DevMessaging;
